import { User, Phone, Mail, Pencil, Trash2, MoreVertical, KeyRound } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from "@/components/ui/tooltip";
import type { TeamMember } from "@/hooks/useTeam";
import type { Database } from "@/integrations/supabase/types";

type AppRole = Database["public"]["Enums"]["app_role"];

interface TeamMemberCardProps {
  member: TeamMember;
  getRoleLabel: (role: AppRole) => string;
  onEdit: () => void;
  onRemove: () => void;
  canRemove: boolean;
}

const roleVariants: Record<AppRole, "default" | "secondary" | "outline" | "destructive"> = {
  super_admin: "destructive",
  restaurant_owner: "default",
  manager: "default",
  cashier: "secondary",
  waiter: "outline",
  kitchen: "secondary",
};

export function TeamMemberCard({ 
  member, 
  getRoleLabel, 
  onEdit, 
  onRemove,
  canRemove 
}: TeamMemberCardProps) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
              <User className="h-5 w-5 text-primary" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-medium text-foreground truncate">
                {member.profile?.full_name || "Sem nome"}
              </p>
              {member.email && (
                <div className="flex items-center gap-1 text-sm text-muted-foreground">
                  <Mail className="h-3 w-3 flex-shrink-0" />
                  <span className="truncate">{member.email}</span>
                </div>
              )}
              {member.profile?.phone && (
                <div className="flex items-center gap-1 text-sm text-muted-foreground">
                  <Phone className="h-3 w-3 flex-shrink-0" />
                  <span>{member.profile.phone}</span>
                </div>
              )}
            </div>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={onEdit}>
                <Pencil className="h-4 w-4 mr-2" />
                Editar
              </DropdownMenuItem>
              {canRemove && (
                <DropdownMenuItem 
                  onClick={onRemove}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Remover da equipe
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div className="mt-3 flex items-center gap-2">
          <Badge variant={roleVariants[member.role] || "secondary"}>
            {getRoleLabel(member.role)}
          </Badge>
          {member.operator_pin && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center justify-center h-5 w-5 rounded-full bg-primary/10">
                    <KeyRound className="h-3 w-3 text-primary" />
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>PIN do PDV configurado</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
